import {
  Card,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Radio,
  Row,
  Space,
  Switch,
  Tag,
  TimePicker,
  Typography,
} from 'antd'
import { useEffect, useState } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { numberRule, requiredRule } from '../../../helpers/rules'
import { selectSelectedAutomation } from '../../../store/AutomationReducer'
import { ClockCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { AnswerHelp } from '../Help'
import { RadioChangeEvent } from 'antd/lib'
import dayjs from 'dayjs'
import {
  AutomationStatus,
  getAutomationStatus,
} from '../../../models/Automation'
import i18n from '../../../i18n/setupLocalization'
import LocalizationKeys from '../../../i18n/LocalizationKeys'
import { capitalize } from '../../../helpers/String'

const { Text } = Typography
const { RangePicker } = DatePicker

interface Props {
  form: FormInstance
}

const Update = ({ form }: Props) => {
  const item = useAppSelector(selectSelectedAutomation())
  const { isOpen: SBRMIsOpen } = useAppSelector((state) => state.SBRM)
  const [liveState, setActiveState] = useState<AutomationStatus | undefined>(
    undefined
  )

  const handleActiveStateChange = (e: RadioChangeEvent) => {
    const selectedState: AutomationStatus = e.target.value
    setActiveState(selectedState)
    switch (selectedState) {
      case 'live':
        form.setFieldValue('isActive', true)
        break
      case 'scheduled':
        form.setFieldValue('isActive', true)
        break
      case 'paused':
        form.setFieldValue('isActive', false)
        break
      default:
        form.setFieldValue('isActive', false)
        break
    }
  }
  const handleScheduledDatesChange = (date: any) => {
    form.setFieldValue('scheduledFrom', date[0])
    form.setFieldValue('scheduledTo', date[1])
  }

  const minutesInHour = (diffMins: number) => {
    var h = Math.floor(diffMins / 60)
    var hours = h > 0 ? (h == 1 ? '1 hour' : h + ' hours') : ''

    var mins = diffMins % 60
    return hours + (mins > 1 ? mins + ' minutes.' : '')
  }

  useEffect(() => {
    if (!SBRMIsOpen) {
      // We want to trigger only the reset
      // when the SBRM opens
      return
    }

    form.setFieldsValue(item)
    if (item?.scheduledFrom && item.scheduledTo) {
      form.setFieldValue('scheduledDates', [
        dayjs(item?.scheduledFrom),
        dayjs(item?.scheduledTo),
      ])
    }
  }, [SBRMIsOpen])

  useEffect(() => {
    if (item == undefined) {
      return
    }
    setActiveState(getAutomationStatus(item))
  }, [item])

  if (!item) {
    return <></>
  }

  return (
    <Form form={form} layout="vertical">
      <Form.Item
        name="id"
        required
        rules={[requiredRule()]}
        style={{ display: 'none' }}
      >
        <Input type="hidden" />
      </Form.Item>
      <Space className="w-full" direction="vertical">
        <Text>Automation status</Text>
        <Card style={{ background: '#f7f3ff', borderColor: '#a58aeb' }}>
          <Radio.Group onChange={handleActiveStateChange} value={liveState}>
            <Space direction="vertical">
              <Radio value={'paused'}>
                {capitalize(
                  i18n.t(LocalizationKeys.Page.AutomationPage.StatusPaused)
                )}
              </Radio>
              <Radio value={'live'}>
                {capitalize(
                  i18n.t(LocalizationKeys.Page.AutomationPage.StatusLive)
                )}
              </Radio>
              <Radio value={'scheduled'}>
                <Space>
                  {capitalize(
                    i18n.t(LocalizationKeys.Page.AutomationPage.StatusScheduled)
                  )}
                  {liveState === 'scheduled' && (
                    <Form.Item name="scheduledDates" className="m-0">
                      <RangePicker
                        showTime={{ format: 'HH:mm' }}
                        onChange={handleScheduledDatesChange}
                      />
                    </Form.Item>
                  )}
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
          <Form.Item
            name="isActive"
            required
            rules={[requiredRule()]}
            style={{ display: 'none' }}
          >
            <Input type="hidden" />
          </Form.Item>
          {liveState == 'scheduled' && (
            <>
              <Form.Item name="scheduledFrom" style={{ display: 'none' }}>
                <Input type="hidden" />
              </Form.Item>
              <Form.Item name="scheduledTo" style={{ display: 'none' }}>
                <Input type="hidden" />
              </Form.Item>
            </>
          )}
        </Card>
      </Space>
      <Divider />
      <Form.Item
        name="shouldReact"
        valuePropName="checked"
        rules={[requiredRule()]}
        label={i18n.t(
          LocalizationKeys.Component.Automation.Update.ReactionLabel
        )}
      >
        <Switch />
      </Form.Item>
      <AnswerHelp
        text={i18n.t(LocalizationKeys.Component.Automation.Update.ReactionHelp)}
      />
      <Divider />
      <Form.Item
        name="delayBetweenMessages"
        rules={[requiredRule(), numberRule()]}
        label={i18n.t(
          LocalizationKeys.Component.Automation.Update.MessageDelayLabel
        )}
      >
        <InputNumber
          min={0}
          max={15}
          className="w-full"
          addonBefore={<ClockCircleOutlined />}
          addonAfter={i18n.t(LocalizationKeys.Misc.Form.Seconds)}
        />
      </Form.Item>
      <AnswerHelp
        text={i18n.t(
          LocalizationKeys.Component.Automation.Update.MessageDelayHelp
        )}
      />
      <Divider />
      <Row gutter={[16, 0]}>
        <Col span={24} style={{ marginBottom: 16 }}>
          {i18n.t(LocalizationKeys.Component.Automation.Update.ReplyDelayLabel)}
        </Col>
        <Col sm={12} xs={24}>
          <Form.Item
            name="followerReplyDelay"
            rules={[requiredRule(), numberRule()]}
          >
            <InputNumber
              min={0}
              max={2880}
              className="w-full"
              addonBefore={i18n.t(
                LocalizationKeys.Component.Automation.Update.Follower
              )}
              addonAfter={i18n.t(LocalizationKeys.Misc.Form.Minutes)}
            />
          </Form.Item>
        </Col>
        <Col sm={12} xs={24}>
          <Form.Item
            name="notFollowerReplyDelay"
            rules={[requiredRule(), numberRule()]}
          >
            <InputNumber
              min={0}
              max={2880}
              className="w-full"
              addonBefore={i18n.t(
                LocalizationKeys.Component.Automation.Update.NonFollower
              )}
              addonAfter={i18n.t(LocalizationKeys.Misc.Form.Minutes)}
            />
          </Form.Item>
        </Col>
      </Row>

      <AnswerHelp
        text={i18n.t(
          LocalizationKeys.Component.Automation.Update.ReplyDelayHelp
        )}
      />
      <Divider />

      <Row gutter={[16, 0]}>
        <Col span={24} style={{ marginBottom: 16 }}>
          {i18n.t(LocalizationKeys.Component.Automation.Update.ReplyLimitLabel)}
        </Col>
        <Col sm={12} xs={24}>
          <Form.Item
            name="followerTimeLimiter"
            rules={[requiredRule(), numberRule()]}
          >
            <InputNumber
              min={0}
              max={2880}
              className="w-full"
              addonBefore={i18n.t(
                LocalizationKeys.Component.Automation.Update.Follower
              )}
              addonAfter={i18n.t(LocalizationKeys.Misc.Form.Minutes)}
            />
          </Form.Item>
        </Col>
        <Col sm={12} xs={24}>
          <Form.Item
            name="notFollowerTimeLimiter"
            rules={[requiredRule(), numberRule()]}
          >
            <InputNumber
              min={0}
              max={2880}
              className="w-full"
              addonBefore={i18n.t(
                LocalizationKeys.Component.Automation.Update.NonFollower
              )}
              addonAfter={i18n.t(LocalizationKeys.Misc.Form.Minutes)}
            />
          </Form.Item>
        </Col>
      </Row>
      <AnswerHelp
        text={i18n.t(
          LocalizationKeys.Component.Automation.Update.ReplyLimitHelp
        )}
      />
    </Form>
  )
}

export default Update
